import useQuery from "utils/hooks/useQuery";
import { useSelector } from "react-redux";
import { selectWorkspace } from "@shared/slices/common.slice";
import { WorkspaceType } from "@shared/types";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import AccountsList from "features/onboarding/overview/accountsList/AccountsList";
import React, { useContext } from "react";
import DefaultGroupConfigurationMessage from "features/bots/BotForm/common/DefaultGroupConfigurationMessage";
import useSignalsBotOverview from "@shared/features/onboarding/hooks/useSignalsBotOverview";
import AmountSelector from "features/bots/BotForm/General/AmountSelector";
import styled, { useTheme } from "styled-components";
import SaveBotButton from "features/bots/common/SaveBotButton";
import PhoneActionBar from "features/onboarding/common/PhoneActionBar";
import { DcaBotCreateeditOrigin } from "analytics/events";
import Link from "common/Typography/Link";
import { GroupOverviewContext } from "@shared/features/onboarding/GroupOverview.context";
import { openMessage } from "utils/misc";
import Flex from "common/Boxes/Flex";
import { OnboardingStyles } from "features/onboarding/common/styles";
import ConfirmCheckbox from "features/onboarding/overview/signalsGroup/ConfirmCheckbox";
import { SignalsBotOverviewProps } from "@shared/features/newMarketplace/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { ONBOARDING_SKIP_CREATE_BOT_BUTTON_ID } from "testIds.ts";
import FollowFuturesAsSpot from "features/bots/BotForm/Advanced/FollowFuturesAsSpot";
import NoGroupSignalsSuggestion from "features/bots/BotForm/common/NoGroupSignalsSuggestion";

const SignalsBotOverview = ({
  groupData,
  onFinishOnboarding,
  getConfirmMessage,
  subscribeGroup,
  skipText,
  onSkipClick,
}: SignalsBotOverviewProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  const theme = useTheme();
  const queryExchange = useQuery().get("exchange") || undefined;
  const isDemo = useSelector(selectWorkspace) === WorkspaceType.DEMO;
  const preferredExchange = isDemo ? queryExchange : undefined;
  const { selectedAccountId } = useContext(GroupOverviewContext)!;

  const {
    futuresAsSpot,
    selectedAccount,
    isCreateSignalsBotDisabled,
    isFormReady,
    isMyGroup,
    setIsConfirmCheckboxChecked,
    showCheckboxError,
    setShowCheckboxError,
    isLoading,
    uncheckedCheckboxError,
  } = useSignalsBotOverview({
    openMessage,
    groupData,
    selectedAccountId,
  });

  const defaultGroupConfiguration = (
    <DefaultGroupConfigurationMessage
      style={{ margin: "16px 0" }}
      subGroupId={groupData?.id}
      exchange={selectedAccount ? selectedAccount.exchange : undefined}
      isOnboarding={true}
    />
  );

  const createSignalsBotButton = (
    <SaveBotButton
      actionLabel={t(
        isDemo ? "guided.start_demo_bot" : "guided.start_live_bot"
      )}
      disabled={isCreateSignalsBotDisabled}
      analyticsBotOriginType={DcaBotCreateeditOrigin.CREATE}
      errorMessage={t(
        !selectedAccount
          ? "bot_overview.select_account_error"
          : !isFormReady
          ? "bot_overview.settings_loading_error"
          : "bot_overview.resolve_issues_error"
      )}
      buttonSize="large"
      style={{ margin: "16px 0" }}
      onSuccess={onFinishOnboarding}
      onSaveClick={subscribeGroup}
      onDisabledClick={() => {
        if (uncheckedCheckboxError && !showCheckboxError) {
          setShowCheckboxError(true);
        }
      }}
    />
  );

  const confirmMessage = (
    <ConfirmCheckbox
      setIsChecked={setIsConfirmCheckboxChecked}
      showCheckboxError={showCheckboxError}
      setShowCheckboxError={setShowCheckboxError}
    />
  );

  const myGroupConfirmMessage = (
    <OnboardingStyles.ConfirmMessage style={{ marginBottom: 8 }}>
      {getConfirmMessage()}
    </OnboardingStyles.ConfirmMessage>
  );

  const buttons = (
    <ButtonsWrapper>
      {/* Phones: pinned to the bottom edge like the other review steps. */}
      <PhoneActionBar phoneOnly={true}>{createSignalsBotButton}</PhoneActionBar>
      {isMyGroup && myGroupConfirmMessage}
      <Link
        onClick={onSkipClick}
        data-testid={ONBOARDING_SKIP_CREATE_BOT_BUTTON_ID}
      >
        {skipText}
      </Link>
    </ButtonsWrapper>
  );

  return (
    <LoadingSpinnerOverlay
      isLoading={isLoading}
      hideContentWhileLoading={true}
      backgroundColor={theme.colors.cardBgColor}
      spinnerSize="large"
    >
      <AccountsList
        groupData={groupData}
        isOnboarding={true}
        preferredExchange={preferredExchange}
      />
      <NoGroupSignalsSuggestion exchange={selectedAccount?.exchange} />
      {defaultGroupConfiguration}
      <FollowFuturesAsSpot
        data={futuresAsSpot}
        style={{ margin: "0 0 16px" }}
      />
      <AmountSelector />
      {!isMyGroup && confirmMessage}
      {buttons}
    </LoadingSpinnerOverlay>
  );
};

export default SignalsBotOverview;

const ButtonsWrapper = styled(Flex)`
  width: 100%;
  flex-direction: column;
  text-align: center;
  /* Phones: the button is in the bottom bar; the skip link keeps its
     distance from the field above. */
  @media (max-width: 560px) {
    padding-top: 16px;
  }
`;
